import React from "react";

import { Stack } from "@mui/material";
import { Box } from "@mui/system";

import { useTheme } from "@fartherfinance/frontend/theme/ThemeProvider";

import Button from "@src/multiCustodian/components/MUI/Button/Button";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";
import Modal from "@src/sharedComponents/Modal/Modal";
import Text from "@src/sharedComponents/Typography/Text";

interface Props {
  isOpen: boolean;
  isMutating: boolean;
  closeModal: () => void;
  onConfirm: () => void;
}

const EquivalentSecuritiesModalV2: React.FC<Props> = ({
  closeModal,
  isMutating,
  onConfirm,
  isOpen,
}) => {
  const {
    color: { $surface, $border },
  } = useTheme();

  return (
    <Modal
      isOpen={isOpen}
      closeModal={closeModal}
      modalStyle={{ width: "520px" }}
    >
      <Text variant="title2" style={{ paddingBottom: 8 }}>
        Enable equivalent securities protection?
      </Text>
      <Text color="textSubtle" variant="body" style={{ paddingBottom: 4 }}>
        Beginning with the next daily trading cycle, Farther will attempt to
        prevent trades that involve selling one security to buy another with
        substantially similar investment exposure.
      </Text>

      <Text color="textSubtle" variant="body" style={{ paddingBottom: 4 }}>
        Determining which securities are sufficiently similar is at Farther's
        discretion. You accept the risk that these securities may differ in
        practice.
      </Text>

      <Text color="textSubtle" variant="body" style={{ paddingBottom: 32 }}>
        You will see a modified version of your target model portfolio (appended
        with "ES") which includes the appropriate security swaps.
      </Text>

      <Stack
        style={{
          border: `1px solid ${$border}`,
          backgroundColor: $surface,
          borderRadius: 5,
          padding: 16,
          marginBottom: 32,
        }}
      >
        <Text variant="bodyBold" style={{ paddingBottom: 8 }}>
          Farther’s investment overlays
        </Text>
        <Text variant="body" color="textSubtle" style={{ paddingBottom: 8 }}>
          By enabling equivalent securities protection, you are opting in to
          Farther’s investment overlays. Please reach out to client services
          with any questions about terms, conditions and fees associated with
          enabling overlays.
        </Text>
      </Stack>

      <Stack direction={"row"} spacing={2}>
        <Button
          disabled={isMutating}
          sx={{ flex: 1 }}
          style={{ width: "100%" }}
          variant={"outlined"}
          buttonType={"secondary"}
          text={"Cancel"}
          onClick={closeModal}
        />
        <Button
          disabled={isMutating}
          sx={{ flex: 1 }}
          style={{ width: "100%" }}
          variant={"contained"}
          buttonType={"primary"}
          text={"Confirm"}
          onClick={onConfirm}
        />
      </Stack>

      {isMutating && (
        <div>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LogoLoadingStill onTop />
          </Box>
        </div>
      )}
    </Modal>
  );
};

export default EquivalentSecuritiesModalV2;
